import { useCallback, useEffect, useMemo, useState } from 'react';
import { PieChart, Plus } from 'lucide-react';
import { api } from '../api/client';
import { useSocle } from '../hooks/useSocle';
import { fmtCHF, fmtPct } from '../utils/format';
import { fonctionEffective } from '../utils/fonctions';
import {
  Grille, LigneEmploye, Categorie, ScenarioBudget, MOIS_LABELS,
} from '../types';
import { EmployeModal } from '../components/EmployeModal';
import { NouvelEmployeModal } from '../components/NouvelEmployeModal';
import { EditionRapide } from '../components/EditionRapide';
import { SelectAnnee } from '../components/SelectAnnee';

type Affichage = 'montant' | 'taux' | 'nominal';

export function SalairesPage() {
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  // Scénario nommé traversant (null = décisions seules) — même mécanique que
  // Revenus, Loyers et Charges depuis le 18.08.2026
  // undefined = socle pas encore connu ; la grille attend avant de charger
  const socleId = useSocle();
  const [scenarioId, setScenarioId] = useState<number | null | undefined>(undefined);
  const [scenarios, setScenarios] = useState<ScenarioBudget[]>([]);
  const [affichage, setAffichage] = useState<Affichage>('montant');
  const [recherche, setRecherche] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState<string>('toutes');
  const [masquerInactifs, setMasquerInactifs] = useState(false);
  const [avecCharges, setAvecCharges] = useState(false);
  const [grille, setGrille] = useState<Grille | null>(null);
  const [categories, setCategories] = useState<Categorie[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [lignesDepliees, setLignesDepliees] = useState<Set<number>>(new Set());
  const [employeOuvert, setEmployeOuvert] = useState<{ id: number; mois?: number } | null>(null);
  const [creationOuverte, setCreationOuverte] = useState(false);
  const [editionRapide, setEditionRapide] = useState<{
    id: number;
    mois: number;
    ancre: { top: number; bottom: number; left: number; right: number };
  } | null>(null);

  const chargerGrille = useCallback(() => {
    if (scenarioId === undefined) return; // on attend de connaître le socle
    setErreur(null);
    api.get<Grille>(`/salaires/grille?annee=${annee}&scenario=${scenarioId ?? 'definitif'}`)
      .then(setGrille)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee, scenarioId]);

  useEffect(() => {
    if (scenarioId === undefined && socleId !== undefined) setScenarioId(socleId);
  }, [socleId, scenarioId]);

  useEffect(() => { chargerGrille(); }, [chargerGrille]);
  useEffect(() => {
    api.get<Categorie[]>('/salaires/categories').then(setCategories).catch(() => {});
    api.get<ScenarioBudget[]>('/scenarios').then(setScenarios).catch(() => {});
  }, []);

  const nomCategorie = useMemo(() => {
    const parNom = new Map(categories.map((c) => [c.id, c]));
    return (id: number): string => {
      const c = parNom.get(id);
      if (!c) return `#${id}`;
      const parent = c.parentId ? parNom.get(c.parentId) : null;
      return parent ? `${parent.nom} — ${c.nom}` : c.nom;
    };
  }, [categories]);

  const scenariosActifs = useMemo(() => scenarios.filter((s) => !s.archive), [scenarios]);
  const variantes = useMemo(
    () => scenariosActifs.filter((s) => s.id !== socleId), [scenariosActifs, socleId]
  );

  // Mois courant (1..12) si l'année affichée est l'année en cours, sinon 0/13
  const moisCourant = annee === maintenant.getFullYear()
    ? maintenant.getMonth() + 1
    : annee < maintenant.getFullYear() ? 13 : 0;
  // Mois de référence pour dériver la fonction depuis la répartition
  const moisRef = Math.min(Math.max(moisCourant, 1), 12);

  const facteurCharges = avecCharges && grille ? 1 + grille.tauxChargesSociales : 1;

  // Fonctions effectives (dérivées de la répartition, repli : catégorie importée)
  const fonctionsPresentes = useMemo(() => {
    if (!grille) return [];
    const s = new Set<string>();
    for (const e of grille.employes) s.add(fonctionEffective(e, moisRef, categories).nom);
    return [...s].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [grille, categories, moisRef]);

  const employesFiltres = useMemo(() => {
    if (!grille) return [];
    const q = recherche.trim().toLowerCase();
    return grille.employes.filter((e) => {
      if (q && !e.nom.toLowerCase().includes(q)) return false;
      if (filtreCategorie !== 'toutes' && fonctionEffective(e, moisRef, categories).nom !== filtreCategorie) return false;
      if (masquerInactifs && e.totalAnnuel === 0) return false;
      return true;
    });
  }, [grille, recherche, filtreCategorie, masquerInactifs, categories, moisRef]);

  // Totaux sur la sélection filtrée
  const totaux = useMemo(() => {
    const parMois = new Array(12).fill(0);
    for (const e of employesFiltres) {
      for (let m = 0; m < 12; m++) parMois[m] += e.mois[m].montant;
    }
    return { parMois, annuel: parMois.reduce((s, v) => s + v, 0) };
  }, [employesFiltres]);

  const kpis = useMemo(() => {
    const passe = totaux.parMois.slice(0, Math.max(0, moisCourant - 1)).reduce((s, v) => s + v, 0);
    const courant = moisCourant >= 1 && moisCourant <= 12 ? totaux.parMois[moisCourant - 1] : 0;
    const futur = moisCourant <= 12 ? totaux.parMois.slice(moisCourant).reduce((s, v) => s + v, 0) : 0;
    return { passe, courant, futur, total: totaux.annuel };
  }, [totaux, moisCourant]);

  const basculerDepliage = (id: number) => {
    setLignesDepliees((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const classesColonne = (m: number) =>
    m < moisCourant ? ' col-passe' : m === moisCourant ? ' col-courant' : '';

  function renduCellule(ligne: LigneEmploye, m: number) {
    const c = ligne.mois[m - 1];
    const projete = c.tagNominal === 'projete' || c.tagTaux === 'projete';
    const changement = c.changementNominal ?? c.changementTaux;
    const infobulle = [
      `${ligne.nom} — ${MOIS_LABELS[m - 1]} ${annee}`,
      `Nominal 100 % : CHF ${fmtCHF(c.nominal)} · Taux : ${fmtPct(c.taux)}`,
      c.treizieme > 0 ? `Inclut 13ᵉ salaire au prorata : CHF ${fmtCHF(c.treizieme)}` : null,
      c.changementNominal ? `Changement de nominal (${c.changementNominal.tag}) : ${c.changementNominal.commentaire}` : null,
      c.changementTaux ? `Changement de taux (${c.changementTaux.tag}) : ${c.changementTaux.commentaire}` : null,
    ].filter(Boolean).join('\n');

    const principal = affichage === 'montant'
      ? (c.montant !== 0 ? fmtCHF(c.montant) : '—')
      : affichage === 'taux'
        ? (c.taux !== 0 ? fmtPct(c.taux) : '—')
        : (c.nominal !== 0 ? fmtCHF(c.nominal) : '—');
    const secondaire = affichage === 'montant'
      ? (c.montant !== 0 ? `${fmtPct(c.taux)} · ${fmtCHF(c.nominal)}` : '')
      : affichage === 'taux'
        ? (c.montant !== 0 ? fmtCHF(c.montant) : '')
        : (c.taux !== 0 ? fmtPct(c.taux) : '');

    return (
      <td
        key={m}
        className={`cell-mois${classesColonne(m)}${projete ? ' cell-projete' : ''}${c.montant === 0 ? ' cell-vide' : ''}`}
        title={infobulle}
        onDoubleClick={(ev) => {
          const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
          setEditionRapide({
            id: ligne.id, mois: m,
            ancre: { top: r.top, bottom: r.bottom, left: r.left, right: r.right },
          });
        }}
      >
        {changement && <span className={`cell-flag cell-flag--${changement.tag}`} />}
        <span className="cell-principal">{principal}</span>
        {secondaire && <span className="cell-secondaire">{secondaire}{c.treizieme > 0 ? ' · ¹³ᵉ' : ''}</span>}
        {!secondaire && c.treizieme > 0 && <span className="cell-secondaire">¹³ᵉ</span>}
      </td>
    );
  }

  function renduSousLignes(ligne: LigneEmploye) {
    // Sous-lignes de répartition : union des catégories présentes sur l'année
    const catIds = new Set<number>();
    for (const c of ligne.mois) {
      if (c.repartition) Object.keys(c.repartition.lignes).forEach((k) => catIds.add(Number(k)));
    }
    if (catIds.size === 0) {
      return (
        <tr key={`${ligne.id}-rep-vide`} className="sous-ligne">
          <td className="cell-nom sous-ligne-label" colSpan={2}>
            <span className="text-ink-3 text-sm">Aucune répartition par fonction — cliquer sur la ligne pour en définir une</span>
          </td>
          <td colSpan={13} />
        </tr>
      );
    }
    const ids = [...catIds].sort((a, b) => nomCategorie(a).localeCompare(nomCategorie(b), 'fr'));
    const lignes = ids.map((catId) => (
      <tr key={`${ligne.id}-rep-${catId}`} className="sous-ligne">
        <td className="cell-nom sous-ligne-label" colSpan={2}>{nomCategorie(catId)}</td>
        {Array.from({ length: 12 }, (_, i) => {
          const c = ligne.mois[i];
          const part = c.repartition?.lignes[catId] ?? 0;
          const projete = c.repartition?.tag === 'projete';
          return (
            <td key={i} className={`cell-mois${classesColonne(i + 1)}${projete ? ' cell-projete' : ''}${part === 0 ? ' cell-vide' : ''}`}>
              <span className="cell-principal">
                {part !== 0 ? (affichage === 'montant' ? fmtCHF(part * c.nominal) : fmtPct(part)) : '—'}
              </span>
            </td>
          );
        })}
        <td className="cell-total" />
      </tr>
    ));
    // Ligne d'écart si une somme ne colle pas au taux du mois
    const aEcart = ligne.mois.some((c) => c.repartition && Math.abs(c.repartition.ecart) > 0.0005);
    if (aEcart) {
      lignes.push(
        <tr key={`${ligne.id}-rep-ecart`} className="sous-ligne sous-ligne-ecart">
          <td className="cell-nom sous-ligne-label" colSpan={2}>Écart répartition − taux</td>
          {Array.from({ length: 12 }, (_, i) => {
            const c = ligne.mois[i];
            const ecart = c.repartition ? c.repartition.ecart : 0;
            return (
              <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                {Math.abs(ecart) > 0.0005
                  ? <span className="text-err font-medium">{ecart > 0 ? '+' : ''}{fmtPct(ecart)}</span>
                  : <span className="text-ink-3">✓</span>}
              </td>
            );
          })}
          <td className="cell-total" />
        </tr>
      );
    }
    return lignes;
  }

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Salaires</h1>
          <span className="stat-pill">{employesFiltres.length} employé·e{employesFiltres.length > 1 ? 's' : ''}</span>
          <span className="stat-pill">CHF {fmtCHF(totaux.annuel * facteurCharges)} / {annee}</span>
          {scenarioId == null
            ? <span className="stat-pill" style={{ color: 'var(--ink-2)' }}
                title="Décisions arrêtées seulement — les mois à venir n'y sont qu'un report">décidé seulement</span>
            : scenarioId === socleId
              ? <span className="stat-pill" style={{ color: 'var(--accent)' }}
                  title="Le budget en l'état : décisions arrêtées plus hypothèses retenues">budget</span>
              : <span className="stat-pill" style={{ color: 'var(--warn)' }}>
                  variante · {scenarios.find((s) => s.id === scenarioId)?.nom ?? '—'}
                </span>}
        </div>
        <div className="flex gap-8 items-center">
          <SelectAnnee annee={annee} onChange={setAnnee} />
          <button className="btn btn-accent" onClick={() => setCreationOuverte(true)}>
            <Plus className="icon-sm" /> Employé·e
          </button>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <div className="kpi-row mb-16">
        <div className="kpi-card">
          <span className="kpi-card__label">Payé à ce jour{moisCourant >= 2 && moisCourant <= 13 ? ` (jan – ${MOIS_LABELS[Math.min(moisCourant, 13) - 2].toLowerCase()})` : ''}</span>
          <span className="kpi-card__value">{fmtCHF(kpis.passe * facteurCharges)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card kpi-card--expanded">
          <span className="kpi-card__label">Mois en cours{moisCourant >= 1 && moisCourant <= 12 ? ` (${MOIS_LABELS[moisCourant - 1].toLowerCase()})` : ''}</span>
          <span className="kpi-card__value">{fmtCHF(kpis.courant * facteurCharges)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">À payer (reste de l'année)</span>
          <span className="kpi-card__value">{fmtCHF(kpis.futur * facteurCharges)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Total {annee}{avecCharges ? ' · charges incl.' : ' · brut'}</span>
          <span className="kpi-card__value">{fmtCHF(kpis.total * facteurCharges)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
      </div>

      <div className="toolbar">
        <div className="filter-chips">
          <button className={`chip${filtreCategorie === 'toutes' ? ' active' : ''}`} onClick={() => setFiltreCategorie('toutes')}>Toutes</button>
          {fonctionsPresentes.map((c) => (
            <button key={c} className={`chip${filtreCategorie === c ? ' active' : ''}`} onClick={() => setFiltreCategorie(c)}>{c}</button>
          ))}
        </div>

        <div className="flex gap-8 items-center" title="Décisions prises seules, ou avec les hypothèses d'un scénario nommé">
          <div className="selectable-group">
            <button
              className={`selectable${scenarioId != null && scenarioId === socleId ? ' active' : ''}`}
              disabled={socleId == null}
              title="Le budget en l'état : décisions arrêtées plus hypothèses retenues"
              onClick={() => setScenarioId(socleId ?? null)}
            >
              Budget
            </button>
            <button
              className={`selectable${scenarioId != null && scenarioId !== socleId ? ' active' : ''}`}
              disabled={variantes.length === 0}
              title={variantes.length === 0 ? 'Aucune variante — en créer une dans Scénarios' : 'Une variante du budget'}
              onClick={() => setScenarioId(variantes[0]?.id ?? null)}
            >
              Variante
            </button>
            <button
              className={`selectable${scenarioId == null ? ' active' : ''}`}
              title="Décisions arrêtées seulement, sans aucune hypothèse — les mois à venir n'y sont qu'un report"
              onClick={() => setScenarioId(null)}
            >
              Décidé seulement
            </button>
          </div>
          {scenarioId != null && scenarioId !== socleId && (
            <select className="sort-select" value={scenarioId} onChange={(e) => setScenarioId(Number(e.target.value))}>
              {variantes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
        </div>

        <div className="selectable-group" title="Valeur affichée dans les cellules">
          <button className={`selectable${affichage === 'montant' ? ' active' : ''}`} onClick={() => setAffichage('montant')}>Montant</button>
          <button className={`selectable${affichage === 'taux' ? ' active' : ''}`} onClick={() => setAffichage('taux')}>Taux</button>
          <button className={`selectable${affichage === 'nominal' ? ' active' : ''}`} onClick={() => setAffichage('nominal')}>Nominal</button>
        </div>

        <label className="toggle-label" title="Ajouter les charges sociales patronales aux totaux">
          <span className="toggle">
            <input type="checkbox" className="toggle-input" checked={avecCharges} onChange={(e) => setAvecCharges(e.target.checked)} />
            <span className="toggle-slider" />
          </span>
          <span className="text-sm">Charges sociales ({grille ? fmtPct(grille.tauxChargesSociales) : '20 %'})</span>
        </label>

        <label className="toggle-label" title="Masquer les lignes sans paiement sur l'année">
          <span className="toggle">
            <input type="checkbox" className="toggle-input" checked={masquerInactifs} onChange={(e) => setMasquerInactifs(e.target.checked)} />
            <span className="toggle-slider" />
          </span>
          <span className="text-sm">Actif·ves seulement</span>
        </label>

        <div className="search-wrapper">
          <input
            className="search-input"
            placeholder="Rechercher un·e employé·e…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
          />
        </div>
      </div>

      <div className="grille-container">
        <table className="table table-compact grille-salaires">
          <thead>
            <tr>
              <th className="cell-nom">Employé·e</th>
              <th className="cell-fonction">Fonction</th>
              {MOIS_LABELS.map((label, i) => (
                <th key={label} className={`cell-mois${classesColonne(i + 1)}`}>
                  {label}
                  {i + 1 === moisCourant && <span className="marqueur-courant" title="Mois en cours" />}
                </th>
              ))}
              <th className="cell-total">Total {annee}</th>
            </tr>
          </thead>
          <tbody>
            {employesFiltres.map((ligne) => (
              <FragmentLigne
                key={ligne.id}
                ligne={ligne}
                fonction={fonctionEffective(ligne, moisRef, categories)}
                depliee={lignesDepliees.has(ligne.id)}
                onDeplier={() => basculerDepliage(ligne.id)}
                onOuvrir={() => setEmployeOuvert({ id: ligne.id })}
                renduCellule={renduCellule}
                renduSousLignes={renduSousLignes}
              />
            ))}
            {employesFiltres.length === 0 && (
              <tr><td colSpan={15} className="text-center text-ink-3 p-16">Aucun résultat pour ce filtre.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="ligne-totaux">
              <td className="cell-nom" colSpan={2}>Masse salariale{avecCharges ? ' + charges' : ''}</td>
              {totaux.parMois.map((t, i) => (
                <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                  <span className="cell-principal">{fmtCHF(t * facteurCharges)}</span>
                </td>
              ))}
              <td className="cell-total"><span className="cell-principal">{fmtCHF(totaux.annuel * facteurCharges)}</span></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-xs text-ink-3 mt-8">
        Montants bruts en CHF (13 mensualités : décembre — ou le dernier mois d'activité — inclut le 13ᵉ salaire au prorata, marqué ¹³ᵉ).
        Les valeurs <span className="cell-projete">projetées</span> apparaissent en italique ; un point de couleur marque un changement prenant
        effet ce mois-là (<span className="cell-flag cell-flag--definitif cell-flag--demo" /> définitif, <span className="cell-flag cell-flag--projete cell-flag--demo" /> projeté).
        <strong> Double-cliquer sur une cellule</strong> pour l'édition rapide (ce mois seul, une plage, ou sans fin) ;
        cliquer sur le nom pour le détail complet (historique, répartition par fonction, infos).
      </p>

      {employeOuvert && grille && (
        <EmployeModal
          scenarioId={scenarioId ?? null}
          scenarios={scenarios}
          employeId={employeOuvert.id}
          annee={annee}
          moisInitial={employeOuvert.mois ?? Math.min(Math.max(moisCourant, 1), 12)}
          categories={categories}
          ligne={grille.employes.find((e) => e.id === employeOuvert.id) ?? null}
          onFermer={() => setEmployeOuvert(null)}
          onModifie={chargerGrille}
        />
      )}
      {creationOuverte && (
        <NouvelEmployeModal
          scenarioId={scenarioId ?? null}
          scenarios={scenarios}
          annee={annee}
          onFermer={() => setCreationOuverte(false)}
          onCree={() => { setCreationOuverte(false); chargerGrille(); }}
        />
      )}
      {editionRapide && grille && (() => {
        const l = grille.employes.find((e) => e.id === editionRapide.id);
        return l ? (
          <EditionRapide
            scenarioId={scenarioId ?? null}
            scenarios={scenarios}
            ligne={l}
            mois={editionRapide.mois}
            annee={annee}
            ancre={editionRapide.ancre}
            onFermer={() => setEditionRapide(null)}
            onModifie={chargerGrille}
            onOuvrirFiche={() => {
              setEmployeOuvert({ id: editionRapide.id, mois: editionRapide.mois });
              setEditionRapide(null);
            }}
          />
        ) : null;
      })()}
    </div>
  );
}

function FragmentLigne({ ligne, fonction, depliee, onDeplier, onOuvrir, renduCellule, renduSousLignes }: {
  ligne: LigneEmploye;
  fonction: { nom: string; derive: boolean };
  depliee: boolean;
  onDeplier: () => void;
  onOuvrir: () => void;
  renduCellule: (l: LigneEmploye, m: number) => React.ReactNode;
  renduSousLignes: (l: LigneEmploye) => React.ReactNode;
}) {
  const aEcart = ligne.mois.some((c) => c.repartition && Math.abs(c.repartition.ecart) > 0.0005);
  const repartitionProjetee = ligne.mois.some((c) => c.repartition?.tag === 'projete');
  return (
    <>
      <tr className="ligne-employe">
        <td className="cell-nom">
          <button
            className="btn-deplier"
            onClick={(e) => { e.stopPropagation(); onDeplier(); }}
            title={depliee ? 'Replier la répartition par fonction' : 'Déplier la répartition par fonction'}
          >
            {depliee ? '▾' : '▸'}
          </button>
          <span className="nom-employe" onClick={onOuvrir} title={ligne.note ?? undefined}>
            {ligne.nom}
          </span>
          {ligne.aProjections && (
            <span
              className="badge badge-warn badge-sm"
              title="Salaire ou taux projeté dans le scénario affiché"
            >
              proj.
            </span>
          )}
        </td>
        <td className="cell-fonction">
          <span
            className={`badge badge-sm${fonction.derive ? ' badge-accent' : ''}`}
            title={fonction.derive
              ? 'Fonction dominante dérivée de la répartition par fonction'
              : 'Catégorie contractuelle importée — définir une répartition par fonction pour la remplacer'}
          >
            {fonction.nom}
          </span>
          {ligne.aRepartition && (
            <span title={repartitionProjetee
              ? 'Répartition du temps projetée (scénario affiché) — saisie dans le module RH'
              : 'Répartition du temps définie — saisie dans le module RH'}
            >
              <PieChart
                className="icon-sm icon-repartition"
                aria-label={repartitionProjetee
                  ? 'Répartition du temps projetée'
                  : 'Répartition du temps définie'}
              />
            </span>
          )}
          {aEcart && <span className="badge badge-err badge-sm" title="La répartition par fonction ne boucle pas avec le taux d'occupation">écart</span>}
        </td>
        {Array.from({ length: 12 }, (_, i) => renduCellule(ligne, i + 1))}
        <td className="cell-total">
          <span className="cell-principal">{ligne.totalAnnuel !== 0 ? fmtCHF(ligne.totalAnnuel) : '—'}</span>
        </td>
      </tr>
      {depliee && renduSousLignes(ligne)}
    </>
  );
}
